import React, { useState } from 'react';
import { chunk } from 'lodash';
import { useTheme } from 'hooks/useTheme';
import { useScreenBreakpoint, BreakpointValue } from 'consts/breakpoints';
import { ViewPager } from 'components/view-pager';
import Arrow from 'assets/svg/left.svg';

import { CarouselItem, Chunk, CarouselWrapper, LeftBtn, RightBtn } from './styled';
import { shouldLazilyLoad } from 'utils/gallery';

export const CAROUSEL_LAYOUT = {
  cardsColumnsNumber: {
    1: 12,
    2: 6,
    3: 4,
    4: 3,
    5: 3,
  },
  showNextSlideStart: {
    1: true,
    2: true,
    3: false,
    4: false,
    5: false,
  },
};

const CARDS_IN_SLIDE = {
  1: 1,
  2: 2,
  3: 3,
  4: 3,
  5: 4,
};

const createSlides = (
  children: React.ReactNode,
  screenBreakpoint: BreakpointValue,
  cardsInSlide: Record<BreakpointValue, number> = CARDS_IN_SLIDE
): React.ReactNode[][] => {
  const cards = React.Children.toArray(children);
  return chunk(cards, cardsInSlide[screenBreakpoint]);
};

export interface BaseCardsCarouselProps {
  slideIndex: number;
  hasArrows?: boolean;
  cardsInSlideCount?: Record<BreakpointValue, number>;
  onSlideIndexChange: (index: number) => void;
  alignLastRight?: boolean;
  carouselItemAutoWidth?: boolean;
}

// Please keep this component stateless!
export const BaseCardsCarousel: React.FC<BaseCardsCarouselProps> = (props) => {
  const {
    children,
    slideIndex,
    hasArrows,
    cardsInSlideCount,
    onSlideIndexChange,
    alignLastRight,
    carouselItemAutoWidth,
  } = props;
  const { isRTL } = useTheme();
  const currentScreenBreakpoint = useScreenBreakpoint();
  const slides = createSlides(children, currentScreenBreakpoint, cardsInSlideCount);

  const [ visitedIndexes, setVisitedIndexes ] = useState(new Set<number>([ slideIndex ]));

  if (!visitedIndexes.has(slideIndex)) {
    setVisitedIndexes(new Set([ ...visitedIndexes, slideIndex ]));
  }

  const onPrevClick = () => onSlideIndexChange(slideIndex - 1);
  const onNextClick = () => onSlideIndexChange(slideIndex + 1);

  return (
    <CarouselWrapper className="base-cards-carousel">
      <ViewPager
        isRTL={isRTL}
        verticalAlign="normal"
        align={alignLastRight ? (slideIndex < slides.length - 1 ? 'left' : 'right') : 'left'}
        slideIndex={slideIndex}
        onSlideIndexChange={onSlideIndexChange}
      >
        {slides.map((slide, chunkId) => shouldLazilyLoad(slideIndex, chunkId, slides.length, 1) || visitedIndexes.has(chunkId) ? (
          <Chunk key={chunkId}>
            {slide.map((card, i) => (
              <CarouselItem
                autoWidth={carouselItemAutoWidth || alignLastRight && chunkId === slides.length - 1}
                key={i}
                currentScreenBreakpoint={currentScreenBreakpoint}
                {...CAROUSEL_LAYOUT}
              >
                {card}
              </CarouselItem>
            ))}
          </Chunk>
        ) : <Chunk key={chunkId} />)}
      </ViewPager>
      {hasArrows && currentScreenBreakpoint > 2 ? (
        <>
          {slideIndex > 0 ? (
            <div>
              <LeftBtn dir="prev" onClick={onPrevClick} hasArrows={hasArrows}>
                <Arrow />
              </LeftBtn>
            </div>
          ) : null}
          {slideIndex < slides.length - 1 ? (
            <div>
              <RightBtn dir="next" onClick={onNextClick} hasArrows={hasArrows}>
                <Arrow />
              </RightBtn>
            </div>
          ) : null}
        </>
      ) : null}
    </CarouselWrapper>
  );
};
